import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import axios from 'axios';
import { homesiteInspect } from '../../../constants/routes';

function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

function formatWhen(row) {
  const t = row.inspected_at || row.created_at;
  if (!t) return '-';
  return new Date(t).toLocaleString();
}

function formatDueDate(value) {
  if (value == null || value === '') return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

/** Inspection warning tier (1st / 2nd) from API `warning_number`. */
function formatFailWarning(warningNumber) {
  const w = warningNumber != null ? String(warningNumber).toLowerCase() : '';
  if (w === 'first') return { label: '1st fail', className: 'badge bg-warning text-dark' };
  if (w === 'second') return { label: '2nd fail', className: 'badge bg-danger' };
  return { label: 'None', className: 'text-muted small' };
}

function HomesiteReports() {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const [rows, setRows] = useState([]);
  const [properties, setProperties] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [filterProperty, setFilterProperty] = useState('');
  const [filterTemplate, setFilterTemplate] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reinspectBusyId, setReinspectBusyId] = useState(null);
  const [pdfBusyId, setPdfBusyId] = useState(null);

  const downloadFailuresPdf = async (inspectionId) => {
    setPdfBusyId(inspectionId);
    setError('');
    try {
      const res = await axios.get(`/api/homesite/inspections/${inspectionId}/pdf-failures-report`, {
        headers: authHeader(),
        responseType: 'blob',
      });
      const ctype = (res.headers['content-type'] || '').toLowerCase();
      if (ctype.includes('application/json')) {
        const text = await res.data.text();
        let msg = 'PDF request failed';
        try {
          msg = JSON.parse(text).error || msg;
        } catch {
          msg = text || msg;
        }
        throw new Error(msg);
      }
      const blob =
        res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `homesite-failures-${inspectionId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      let msg = e.message || 'PDF download failed';
      if (e.response?.data instanceof Blob) {
        try {
          const t = await e.response.data.text();
          msg = JSON.parse(t).error || msg;
        } catch {
          /* keep msg */
        }
      } else if (e.response?.data?.error) {
        msg = e.response.data.error;
      }
      setError(msg);
    } finally {
      setPdfBusyId(null);
    }
  };

  const startReinspect = async (sourceId) => {
    setReinspectBusyId(sourceId);
    setError('');
    try {
      const res = await axios.post(
        `/api/homesite/inspections/${sourceId}/reinspect`,
        {},
        { headers: authHeader() }
      );
      const newId = res.data?.id;
      if (!newId) {
        setError('Re-inspect did not return a new inspection id');
        return;
      }
      navigate(homesiteInspect(newId));
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Re-inspect failed');
    } finally {
      setReinspectBusyId(null);
    }
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [pRes, tRes] = await Promise.all([
          axios.get('/api/homesite/picker/properties', { headers: authHeader() }),
          axios.get('/api/homesite/templates', { headers: authHeader() }),
        ]);
        if (cancel) return;
        setProperties(pRes.data);
        setTemplates(tRes.data);
        const params = {};
        if (filterProperty) params.propertyId = filterProperty;
        if (filterTemplate) params.templateId = filterTemplate;
        if (filterStatus) params.status = filterStatus;
        const res = await axios.get('/api/homesite/inspections', {
          headers: authHeader(),
          params,
        });
        if (!cancel) setRows(res.data);
      } catch (e) {
        if (!cancel) setError(e.response?.data?.error || e.message || 'Failed to load');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [filterProperty, filterTemplate, filterStatus]);

  return (
    <div className="app-shell-page">
      <div className="app-page-header mb-4">
        <h2 className="h5 mb-1">Reports</h2>
        <p className="text-muted small mb-0">Filter and open homesite inspections.</p>
      </div>
      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="card app-card mb-4">
        <div className="card-body">
      <div className="row g-2">
        <div className="col-md-3">
          <label className="form-label small mb-0">Community</label>
          <select className="form-select form-select-sm" value={filterProperty} onChange={(e) => setFilterProperty(e.target.value)}>
            <option value="">All</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label small mb-0">Checklist</label>
          <select className="form-select form-select-sm" value={filterTemplate} onChange={(e) => setFilterTemplate(e.target.value)}>
            <option value="">All</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label small mb-0">Status</label>
          <select className="form-select form-select-sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
          </select>
        </div>
      </div>
        </div>
      </div>

      {loading ? (
        <div className="app-loading text-muted" role="status">
          <span className="spinner-border spinner-border-sm" aria-hidden="true" />
          <span>Loading…</span>
        </div>
      ) : (
        <motion.div
          key={`rep-${filterProperty}-${filterTemplate}-${filterStatus}`}
          initial={reduceMotion ? false : { opacity: 0.9, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.22, ease: [0.25, 0.1, 0.25, 1] }}
        >
        <div className="card app-card">
          <div className="card-body p-0">
        <div className="app-table-wrap table-responsive">
          <table className="table table-striped table-hover table-sm mb-0">
            <thead className="table-light">
              <tr>
                <th>Community</th>
                <th>Location</th>
                <th>Resident</th>
                <th>Date</th>
                <th>Inspector</th>
                <th>Failures</th>
                <th>Fail warning</th>
                <th>Due date</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center text-muted py-4 small app-empty-hint">
                    No inspections found
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const failCount = Number(r.fail_count) || 0;
                  const warn = formatFailWarning(r.warning_number);
                  return (
                  <tr key={r.id}>
                    <td>{r.property_name}</td>
                    <td>{r.site_name || '-'}</td>
                    <td>{r.resident_name || '-'}</td>
                    <td>{formatWhen(r)}</td>
                    <td>{r.inspector_name || '-'}</td>
                    <td>
                      {failCount > 0 ? (
                        <span className="badge bg-danger">{failCount === 1 ? '1 fail' : `${failCount} fails`}</span>
                      ) : (
                        <span className="text-muted small">None</span>
                      )}
                    </td>
                    <td>
                      <span className={warn.className}>{warn.label}</span>
                    </td>
                    <td>{formatDueDate(r.due_date_corrections)}</td>
                    <td>
                      <span className={`badge ${r.status === 'submitted' ? 'bg-success' : 'bg-warning text-dark'}`}>
                        {r.status}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-1 justify-content-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-dark"
                          disabled={reinspectBusyId != null || pdfBusyId != null}
                          title="Download PDF: header, failed items, notes, and pictures"
                          onClick={() => downloadFailuresPdf(r.id)}
                        >
                          {pdfBusyId === r.id ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-1" aria-hidden />
                              PDF…
                            </>
                          ) : (
                            'PDF'
                          )}
                        </button>
                        {failCount > 0 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-warning"
                            disabled={reinspectBusyId != null || pdfBusyId != null}
                            title="Open a new draft with only failed items activated"
                            onClick={() => startReinspect(r.id)}
                          >
                            {reinspectBusyId === r.id ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" aria-hidden />
                                Re-inspect…
                              </>
                            ) : (
                              'Re-inspect'
                            )}
                          </button>
                        )}
                        {r.status === 'submitted' ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            disabled={reinspectBusyId != null || pdfBusyId != null}
                            onClick={() => navigate(`${homesiteInspect(r.id)}?view=1`)}
                          >
                            View
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={reinspectBusyId != null || pdfBusyId != null}
                            onClick={() => navigate(homesiteInspect(r.id))}
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
          </div>
        </div>
        </motion.div>
      )}
    </div>
  );
}

export default HomesiteReports;
